import FingerprintJS from '@fingerprintjs/fingerprintjs'
import loginByFacebook from 'api/loginByFacebook'
import loginByGoogle from 'api/loginByGoogle'
import { PlausibleEvent } from 'enums/plausible'
import ErrorCode from 'forms/ErrorCodes'
import handleResponseError from 'forms/handleResponseError'
import LoginForm from 'forms/LoginForm'
import validateMessage from 'forms/validateMessage'
import React, { FC, useCallback, useState } from 'react'
import useLanguageForSave from 'utils/hooks/useLanguageForSave'
import usePlausible from 'utils/hooks/usePlausible'
import setTokensAndRedirect from 'utils/setTokensAndRedirect'

import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import ErrorMessage from 'components/ErrorMessage/ErrorMessage'
import FacebookAuth from 'components/FacebookAuth'
import GoogleAuth from 'components/GoogleAuth/GoogleAuth'
import {
  BanIcon,
  EmailIcon,
  ErrorIcon,
  SocialErrorIcon,
  TimesRoundIcon,
  WiFiIcon,
} from 'components/Images/Icons'

import styles from './LoginFormOrGoogle.module.scss'

const LoginFormOrGoogle = ({ referrer }) => {
  const router = useRouter()
  const setEvent = usePlausible()
  const [socialLoginError, setSocialLoginError] = useState<{ icon?: FC; message: string }>()
  const [loginAsyncError, setLoginAsyncError] = useState<{ icon?: FC; message: string }>()
  const [t, i18n] = useTranslation(['main'])
  const savedLanguage = useLanguageForSave(i18n)

  const onGettingSocialToken = useCallback(
    async ({ providerUserToken }: { providerUserToken: string }, social: 'google' | 'facebook') => {
      try {
        const fp = await FingerprintJS.load()
        const fpResponse = await fp.get()

        const loginBySocialRequest = {
          google: loginByGoogle,
          facebook: loginByFacebook,
        }

        const res = await loginBySocialRequest[social]({
          visitorId: fpResponse.visitorId,
          providerUserToken,
          language: savedLanguage,
        })

        setEvent(PlausibleEvent.ClickedOnSocialSignIn, {
          step: 2,
          referrer,
          social,
        })

        if (res.status === 200) {
          await setEvent(
            PlausibleEvent.Authorised,
            {
              step: 3,
              type: `system`,
              email: res.data.payload?.email,
            },
            true,
          )
          setTokensAndRedirect(res.data.payload.accessToken, res.data.payload.refreshToken)
        } else if (res.status === 404) {
          router.push(`/auth/${social}/${providerUserToken}`)
        } else {
          const { code, payload } = res.data
          setEvent(PlausibleEvent.NotAuthorised, {
            step: 3,
            type: `system`,
            error: payload?.errors[0],
            email: res.data.payload?.email || '',
          })
          switch (code) {
            case ErrorCode.VALIDATION_ERRORS:
              if (payload?.errors) {
                const wrappedErrors = handleResponseError(res)
                setSocialLoginError({ message: wrappedErrors })
              }
              break
            case ErrorCode.INVALID_CREDENTIALS:
              setSocialLoginError({
                icon: TimesRoundIcon,
                message: validateMessage.invalidCredentials,
              })
              break
            case ErrorCode.USER_WITH_SPECIFIED_EMAIL_NOT_FOUND:
              setSocialLoginError({
                icon: SocialErrorIcon,
                message: validateMessage.invalidCredentials,
              })
              break
            case ErrorCode.EMAIL_ALREADY_REGISTER:
              setSocialLoginError({
                icon: EmailIcon,
                message: validateMessage.emailIsAlreadyRegistered,
              })
              break
            case ErrorCode.GEO_RESTRICTED:
              setSocialLoginError({ message: validateMessage.restricted })
              break
            case ErrorCode.SOCIAL_DUPLICATE_USER_EMAIL:
              setSocialLoginError({ icon: EmailIcon, message: validateMessage.duplicateUserEmail })
              break
            case ErrorCode.USER_BANNED:
              setSocialLoginError({ icon: BanIcon, message: validateMessage.banned })
              break
            case ErrorCode.USER_BLOCKED:
              setSocialLoginError({ icon: ErrorIcon, message: validateMessage.userBlocked })
              break
            default:
              setSocialLoginError({ message: validateMessage.serverError })
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)

        setSocialLoginError({ icon: WiFiIcon, message: validateMessage.serverError })
      }
    },
    [router, setSocialLoginError],
  )

  return (
    <div className={styles.grid}>
      <h1 className={styles.h1}>{t('main:authLogin')}</h1>
      <div className={styles.oauth} onClick={() => setLoginAsyncError(undefined)}>
        <GoogleAuth
          id="login-form-google-button"
          buttonText={t('main:authLoginGoogle')}
          callback={(response) => onGettingSocialToken(response, 'google')}
        />

        {process.env.NEXT_PUBLIC_FACEBOOK_APP_ID && (
          <FacebookAuth
            id="login-form-facebook-button"
            buttonText={t('main:authLoginFacebook')}
            onAuthSuccess={(response) => onGettingSocialToken(response, 'facebook')}
          />
        )}

        {socialLoginError && (
          <ErrorMessage icon={socialLoginError.icon} message={socialLoginError.message} />
        )}
      </div>
      <div className={styles.divider}>
        <div className={styles.line} />
        <div className={styles.text}>{t('main:authUseEmail')}</div>
        <div className={styles.line} />
      </div>
      <div className={styles.form}>
        <LoginForm
          referrer={referrer}
          error={loginAsyncError}
          setError={setLoginAsyncError}
          setSocialLoginError={setSocialLoginError}
        />
      </div>
    </div>
  )
}

export default LoginFormOrGoogle
